import React from "react";
import classNames from "classnames";
import styles from "../FormField.module.scss";
export interface IDecimalFieldProps {
  id?: string;
  error?: string;
  name?: string;
  type: string;
  readOnly?: boolean;
  registerField?: any;
  isFocused?: boolean;
  isRequired?: boolean;
  autoComplete?: string;
  onFocus?: (
    e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
  ) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  checksPresenceName?: (
    e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLElement>,
    name: string,
  ) => void;
  allowNegative?: boolean;
  decimalPlaces?: number;
  maxNumberLength?: number;
}

const getPattern = (isNegative = false, isNumber = false) => {
  if (isNumber) {
    return isNegative ? /[^-?0-9]/g : /[^0-9]/g;
  }
  return isNegative ? /[^-?0-9.]/g : /[^0-9.]/g;
};
const checkNegative = (val: string) => {
  return /^-(\d|-)?/.test(val);
};
const replaceDoubleNegative = (val: string) => {
  return checkNegative(val)
    ? "-" + val.replace(/-/g, "")
    : val.replace(/-/g, "");
};

const DecimalFormField: React.FC<IDecimalFieldProps> = ({
  id,
  error,
  name,
  type,
  registerField,
  isFocused,
  onFocus,
  onBlur,
  checksPresenceName,
  autoComplete,
  maxNumberLength,
  readOnly = false,
  allowNegative = false,
  decimalPlaces = 5,
}) => {
  const PATTERN = getPattern(allowNegative, type === "number");

  const getInputNumbersValue = (value: string) => {
    return value.replace(PATTERN, "");
  };

  const clearValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    input.value = getInputNumbersValue(input.value);
  };

  const getValue = (val: string | number) => {
    if (typeof val === "string" || typeof val === "number") {
      const value = `${val}`;
      if (!value.trim()) return "";
      const inputNumbersValue = getInputNumbersValue(value?.replace(",", "."));
      const [start = "", end] = inputNumbersValue.split(".");
      const correctEnd =
        end !== undefined ? `.${end.slice(0, decimalPlaces)}` : "";
      let result = start
        ? `${
            maxNumberLength ? start.slice(0, maxNumberLength) : start
          }${correctEnd}`
        : inputNumbersValue;
      result = replaceDoubleNegative(result);
      if (/\./.test(inputNumbersValue) && !/\./.test(result)) {
        return result + ".";
      }
      return result;
    }
    return "";
  };

  const handleDecimalInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (checksPresenceName) {
      checksPresenceName(e, name);
    }

    const input = e.target;
    const value = getValue(input.value);
    input.value = value;
  };

  const handleDecimalPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData ?? window["clipboardData"];
    const input = e.target as HTMLInputElement;
    const inputNumbersValue = getInputNumbersValue(input.value);

    if (pasted) {
      const pastedText = pasted.getData("Text");
      if (PATTERN.test(pastedText)) {
        const value = getValue(inputNumbersValue);
        input.value = value;
      }
    }
  };

  return (
    <input
      {...registerField}
      name={name}
      id={id}
      className={classNames(styles.input, {
        [styles.error]: error,
        [styles.active]: isFocused,
      })}
      type="text"
      readOnly={readOnly}
      onInput={handleDecimalInput}
      onPaste={handleDecimalPaste}
      onFocus={(e) => {
        onFocus(e);
        clearValue(e);
      }}
      onBlur={(e) => {
        onBlur(e);
        clearValue(e);
      }}
      autoComplete={autoComplete}
    />
  );
};

export default DecimalFormField;
